import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faRobot } from '@fortawesome/free-solid-svg-icons';
import { faGithub } from '@fortawesome/free-brands-svg-icons';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DetailsEditor from './DetailsEditor';
import GitHubConnectionEdit from './GitHubConnectionEdit';
import GitHubAppManagement from './GitHubAppManagement';
import useGithubSshConfigs from '../hooks/useGithubSshConfigs';
import { useTenants } from '../hooks/useTenants';
import { useGitHubInstallations } from '../hooks/useGitHubInstallations';
import GlossaryPanel from './GlossaryPanel';
import { useGlossary } from '../hooks/useGlossary';
import styles from './GitHubConfiguration.module.scss';
import '../styles/shared.scss';

interface GithubSshConfig {
    tenantId: string;
    name: string;
    description?: string;
    repositoryUrl: string;
    accessMethod: 'ssh' | 'pat';
    connectionType?: 'repository' | 'organization' | 'user';
    keyType?: 'rsa' | 'ed25519';
    isActive: boolean;
    secretArn?: string;
    publicKey?: string;
    createdAt: string;
    updatedAt: string;
}

interface GitHubInstallation {
    connectionId: string;
    creationDate: string;
    installationId: string;
    name: string;
    accountLogin: string;
    accountType: 'User' | 'Organization';
    repositorySelection: 'all' | 'selected';
    permissions: any;
    isActive: boolean;
    ownerEmail: string;
    description?: string;
    updatedAt: string;
    isShared?: boolean;
    sharedBy?: string;
    sharedAt?: string;
}

type GitHubConnection = GithubSshConfig | GitHubInstallation;

const GitHubConfigurations: React.FC = () => {
    const { connectionId } = useParams<{ connectionId?: string }>();
    const navigate = useNavigate();
    const { data: githubConnections = [], isLoading: sshLoading, error: sshError } = useGithubSshConfigs();
    const { data: installations = [], isLoading: appLoading, error: appError } = useGitHubInstallations();
    const { data: tenants = [] } = useTenants();
    const { isOpen, scrollToItem, openGlossary, closeGlossary } = useGlossary();
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createType, setCreateType] = useState<'ssh' | 'pat' | 'app'>('ssh');
    const [sidebarWidth, setSidebarWidth] = useState(300);
    const isResizingRef = useRef(false);

    const isLoading = sshLoading || appLoading;
    const error = sshError || appError;

    const getTenantName = (tenantId: string) => {
        const tenant = tenants.find(t => t.id === tenantId);
        return tenant?.name || tenantId;
    };

    const handleCreateConnection = (type: 'ssh' | 'pat' | 'app') => {
        navigate('/connections/github');
        setCreateType(type);
        setShowCreateForm(true);
    };

    const isPATConnection = (connection: GitHubConnection) => {
        return 'repositoryUrl' in connection && connection.accessMethod === 'pat';
    };

    const getFontAwesomeIcon = (connection: GitHubConnection) => {
        if ('repositoryUrl' in connection) {
            return faKey;
        }
        return faRobot;
    };



    const getConnectionName = (connection: GitHubConnection) => {
        if ('repositoryUrl' in connection) {
            return connection.name || connection.repositoryUrl.split('/').slice(-2).join('/');
        }
        return connection.name;
    };

    const getConnectionMeta = (connection: GitHubConnection) => {
        if ('repositoryUrl' in connection) {
            return [
                getTenantName(connection.tenantId),
                connection.accessMethod === 'pat' ? 'PAT' : 'SSH',
                connection.accessMethod === 'pat' ? 'Token' : (connection.keyType?.toUpperCase() || 'SSH')
            ];
        }
        return [
            connection.isShared ? 'Shared' : 'Owned',
            'App',
            connection.accountType
        ];
    };

    const handleConnectionSelect = (connection: GitHubConnection) => {
        const connectionKey = 'repositoryUrl' in connection 
            ? `${connection.accessMethod}-${connection.tenantId}-${btoa(connection.repositoryUrl).replace(/[^a-zA-Z0-9]/g, '')}`
            : `app-${connection.connectionId}`;
        navigate(`/connections/github/${connectionKey}`);
        setShowCreateForm(false);
    };

    const handleFormComplete = () => {
        setShowCreateForm(false);
        navigate('/connections/github');
    };

    const handleSave = () => {
        handleFormComplete();
    };

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isResizingRef.current) return;
        const newWidth = Math.max(250, Math.min(600, e.clientX));
        setSidebarWidth(newWidth);
    }, []);

    const handleMouseUp = useCallback(() => {
        isResizingRef.current = false;
    }, []);

    const handleMouseDown = (e: React.MouseEvent) => {
        isResizingRef.current = true;
        e.preventDefault();
    };

    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    // Filter GitHub App installations to show only owned or shared (prioritize owned)
    const filteredInstallations = installations.reduce((acc, installation) => {
        const existing = acc.find(inst => inst.connectionId === installation.connectionId);
        if (!existing) {
            acc.push(installation);
        } else if (!(installation as any).isShared && (existing as any).isShared) {
            // Replace shared with owned version
            const index = acc.indexOf(existing);
            acc[index] = installation;
        }
        return acc;
    }, [] as typeof installations);

    // Combine and sort connections
    const allConnections: GitHubConnection[] = [
        ...githubConnections,
        ...filteredInstallations
    ].sort((a, b) => getConnectionName(a).localeCompare(getConnectionName(b)));

    // Find selected connection from URL parameter
    const selectedConnection = connectionId ? allConnections.find(conn => {
        const connectionKey = 'repositoryUrl' in conn 
            ? `${conn.accessMethod}-${conn.tenantId}-${btoa(conn.repositoryUrl).replace(/[^a-zA-Z0-9]/g, '')}`
            : `app-${conn.connectionId}`;
        return connectionKey === connectionId;
    }) || null : null;

    return (
        <div className={styles.container}>
            {/* Left Column - Connections List */}
            <div 
                className={styles.sidebar} 
                style={{ width: `${sidebarWidth}px` }}
            >
                <div className={styles.header}>
                    <div className="page-title-group">
                        <h3>
                            <FontAwesomeIcon icon={faGithub} style={{ marginRight: '8px' }} />
                            GitHub
                        </h3>
                        <button
                            onClick={() => openGlossary('GitHub Connection')}
                            className="info-button"
                            title="View definition"
                        >
                            Info
                        </button>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            onClick={() => handleCreateConnection('ssh')}
                            className="create-button"
                            title="Add SSH Connection"
                        >
                            <FontAwesomeIcon icon={faKey} />
                        </button>
                        <button
                            onClick={() => handleCreateConnection('pat')}
                            className="create-button"
                            title="Add PAT Connection"
                        >
                            <VpnKeyIcon />
                        </button>
                        <button
                            onClick={() => handleCreateConnection('app')}
                            className="create-button"
                            title="Add GitHub App"
                        >
                            <FontAwesomeIcon icon={faRobot} />
                        </button>
                    </div>
                </div>
                
                <div>
                    {isLoading ? (
                        <p className={styles.statusText}>
                            Loading connections...
                        </p>
                    ) : error ? (
                        <p className={styles.errorText}>
                            Error loading connections
                        </p>
                    ) : allConnections.length === 0 ? (
                        <p className={styles.statusText}>
                            No connections configured
                        </p>
                    ) : (
                        allConnections.map((connection, index) => (
                            <div
                                key={`${'repositoryUrl' in connection ? `${connection.accessMethod}-${connection.tenantId}-${connection.repositoryUrl}` : `app-${connection.connectionId}`}`}
                                onClick={() => handleConnectionSelect(connection)}
                                className={`${styles.connectionItem} ${selectedConnection === connection ? styles.selected : ''}`}
                            >
                                <div className={styles.connectionHeader}>
                                    {isPATConnection(connection) ? (
                                        <VpnKeyIcon className={styles.accessMethodIcon} />
                                    ) : (
                                        <FontAwesomeIcon 
                                            icon={getFontAwesomeIcon(connection)} 
                                            className={styles.accessMethodIcon}
                                        />
                                    )}
                                    <div className={styles.connectionName}>
                                        {getConnectionName(connection)}
                                    </div>
                                    <div className={`${styles.status} ${connection.isActive ? styles.active : styles.inactive}`}>
                                        {connection.isActive ? 'Active' : 'Inactive'}
                                    </div>
                                </div>
                                <div className={styles.connectionMeta}>
                                    {getConnectionMeta(connection).map((meta, i) => (
                                        <div key={i}>{meta}</div>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Resize Handle */}
            <div 
                className={styles.resizeHandle}
                onMouseDown={handleMouseDown}
            />

            {/* Right Column - Details/Form */}
            <div className={styles.content}>
                {showCreateForm ? (
                    <DetailsEditor
                        title={`Create ${createType === 'ssh' ? 'SSH Connection' : createType === 'pat' ? 'PAT Connection' : 'GitHub App'}`}
                        showDelete={false}
                        onComplete={handleFormComplete}
                    >
                        {createType === 'ssh' ? (
                            <GitHubConnectionEdit 
                                accessMethod="ssh"
                                installations={installations}
                                onSave={handleSave} 
                                onCancel={handleFormComplete} 
                            />
                        ) : createType === 'pat' ? (
                            <GitHubConnectionEdit 
                                accessMethod="pat"
                                installations={installations}
                                onSave={handleSave} 
                                onCancel={handleFormComplete} 
                            />
                        ) : (
                            <GitHubAppManagement />
                        )}
                    </DetailsEditor>
                ) : selectedConnection ? (
                    <DetailsEditor
                        title="GitHub Connection Details"
                        onComplete={handleFormComplete}
                    >
                        {'repositoryUrl' in selectedConnection ? (
                            <GitHubConnectionEdit 
                                connection={selectedConnection}
                                installations={installations}
                                onSave={handleSave}
                                onCancel={handleFormComplete}
                            />
                        ) : (
                            <GitHubAppManagement 
                                selectedInstallation={selectedConnection}
                                isReadOnly={selectedConnection.isShared}
                            />
                        )}
                    </DetailsEditor>
                ) : (
                    <div className={styles.placeholder}>
                        Select a GitHub connection or create a new one
                    </div>
                )}
            </div>
            
            <GlossaryPanel 
                isOpen={isOpen} 
                onClose={closeGlossary} 
                scrollToItem={scrollToItem} 
            />
        </div>
    );
};

export default GitHubConfigurations;